import { useContext, createContext, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../services/supabaseClient';

// Create a context for the Supabase client
export const SupabaseContext = createContext(null);

// Custom hook to use Supabase auth client with better error handling
export const useSupabaseAuthClient = () => {
  const client = useContext(SupabaseContext);
  const { state } = useAuth();
  
  const wrappedClient = client || supabase;
  
  // Create a wrapper that handles session restoration before operations
  const authWrapper = {
    ...wrappedClient.auth,
    
    // Override signOut to handle cases where there's no active session
    signOut: useCallback(async (options) => {
      try {
        // Always attempt signOut regardless of session state
        const result = await wrappedClient.auth.signOut(options);
        
        if (result.error) {
          console.warn('useSupabaseAuthClient: signOut had error (but may be harmless):', result.error);
        }
        
        // Always return success for signOut - local cleanup will happen in AuthContext
        return { error: null };
      } catch (error) {
        console.warn('useSupabaseAuthClient: signOut exception (but continuing):', error);
        // Even if signOut fails, we should allow local cleanup
        return { error: null };
      }
    }, [wrappedClient]),
    
    // Override signInWithPassword with better error handling
    signInWithPassword: useCallback(async (credentials) => {
      try {
        const result = await wrappedClient.auth.signInWithPassword(credentials);
        if (result.error) {
          console.error('Sign in error:', result.error);
        }
        return result;
      } catch (error) {
        console.error('Unexpected sign in error:', error);
        return { data: null, error };
      }
    }, [wrappedClient]),
    
    // Override getSession with retry logic and recovery
    getSession: useCallback(async () => {
      try {
        const result = await wrappedClient.auth.getSession();
        
        // If no session but Supabase might have one in storage, try to recover
        if (!result.data.session && !result.error) {
          // Wait a bit and try again (sometimes Supabase needs time to restore from storage)
          await new Promise(resolve => setTimeout(resolve, 500));
          const retryResult = await wrappedClient.auth.getSession();
          
          if (retryResult.data.session) {
            console.log('useSupabaseAuthClient: Session recovered on retry');
          }
          
          return retryResult;
        }
        
        return result;
      } catch (error) {
        console.error('useSupabaseAuthClient: Error getting session:', error);
        return { data: { session: null }, error };
      }
    }, [wrappedClient]),
    
    // Override updateUser with session check
    updateUser: useCallback(async (attributes) => {
      try {
        // Check if we have a valid session first
        const { data: { session } } = await wrappedClient.auth.getSession();
        
        if (!session) {
          throw new Error('No active session found. Please sign in again.');
        }
        
        const result = await wrappedClient.auth.updateUser(attributes);
        return result;
      } catch (error) {
        console.error('Error updating user:', error);
        return { data: null, error };
      }
    }, [wrappedClient]),
    
    // Keep other auth methods as-is but with error wrapping
    signUp: useCallback(async (credentials) => {
      try {
        const result = await wrappedClient.auth.signUp(credentials);
        return result;
      } catch (error) {
        console.error('Sign up error:', error);
        return { data: null, error };
      }
    }, [wrappedClient]),
    
    onAuthStateChange: useCallback((callback) => {
      // Wrap the callback to ensure it doesn't use await on Supabase methods
      const safeCallback = (event, session) => {
        // Call the original callback but don't await anything
        try {
          const result = callback(event, session);
          // If callback returns a promise, handle it separately to avoid deadlocks
          if (result && typeof result.then === 'function') {
            result.catch(error => {
              console.error('onAuthStateChange callback error (handled safely):', error);
            });
          }
        } catch (error) {
          console.error('onAuthStateChange callback error:', error);
        }
      };
      
      return wrappedClient.auth.onAuthStateChange(safeCallback);
    }, [wrappedClient]),
    resetPasswordForEmail: wrappedClient.auth.resetPasswordForEmail.bind(wrappedClient.auth)
  };
  
  return {
    ...wrappedClient,
    auth: authWrapper
  };
};

// Custom hook to get current user (integrates with AuthContext)
export const useSupabaseUser = () => {
  const { state } = useAuth();
  
  // Return a reactive reference-like object similar to Vue's ref
  return { 
    value: state.user || null,
    // Helper to update the user (this would be managed by dispatch in real usage)
    setValue: (newValue) => {
      // This is a placeholder - actual implementation would dispatch to AuthContext
      console.warn('useSupabaseUser setValue called - should use AuthContext dispatch instead');
    }
  };
};

export default useSupabaseAuthClient;
